import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
  type Tone,
} from '@cartly/ui';
import { Copy, Eye, MessageCircle, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { BizAppBadge, CopyField, Pager, StatusBadge } from '../../components/Common';
import { formatRelative } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { EmbeddedSignup } from './EmbeddedSignup';
import { isFormEditable, TemplateCreate } from './TemplateCreate';
import { CampaignSections } from './Campaigns';
import { countBodyParams, dynamicUrlButton, MessagingSections } from './Messaging';

interface WhatsappStatus {
  configured: boolean;
  graphVersion: string | null;
  appId: string | null;
  embeddedSignup: { configured: boolean; configId: string | null; solutionId: string | null };
  creditLineConfigured: boolean;
  tataCreditConfigured: boolean;
  webhookVerifyTokenSet: boolean;
  webhookUrl: string;
}

interface WhatsappAccountRow {
  id: string;
  wabaId: string;
  name: string;
  method: 'manual_token' | 'embedded_signup';
  status: 'active' | 'disconnected' | 'needs_reauth';
  needsReauth: boolean;
  templatesSyncedAt: string | null;
  webhookSubscribedAt: string | null;
  createdAt: string;
  tokenHint: string | null;
  creditLine: {
    status: 'attached' | 'failed' | 'other_funding' | null;
    creditLineId: string | null;
    allocationConfigId: string | null;
    primaryFundingId: string | null;
    error: string | null;
    checkedAt: string | null;
  };
  phoneNumbers: {
    id: string;
    phoneNumberId: string;
    displayPhoneNumber: string;
    verifiedName: string | null;
    qualityRating: string | null;
    messagingLimitTier: string | null;
    registeredAt: string | null;
    isOnBizApp: boolean;
    platformType: string | null;
    tataCredit: {
      status: 'assigned' | 'failed' | null;
      error: string | null;
      assignedAt: string | null;
    };
  }[];
}

interface WhatsappTemplateRow {
  id: string;
  wabaId: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejectedReason: string | null;
  qualityScore: string | null;
  components: unknown;
  updatedAt: string;
}

const EDITABLE_STATUSES = ['APPROVED', 'REJECTED', 'PAUSED'];

interface PreviewComponent {
  type?: string;
  format?: string;
  text?: string;
  buttons?: Array<{ type?: string; text?: string; url?: string; phone_number?: string }>;
}

/** Read-only look at a template: a chat-bubble preview plus its review details. */
function TemplateView({ t, onClose }: { t: WhatsappTemplateRow | null; onClose: () => void }) {
  const list = (Array.isArray(t?.components) ? t.components : []) as PreviewComponent[];
  const header = list.find((c) => c.type === 'HEADER');
  const body = list.find((c) => c.type === 'BODY');
  const footer = list.find((c) => c.type === 'FOOTER');
  const buttons = list.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
  return (
    <Modal open={!!t} title={t?.name ?? ''} onClose={onClose}>
      {t && (
        <div className="stack">
          <div className="row">
            <Badge tone={TEMPLATE_TONE[t.status] ?? 'neutral'}>
              {t.status.toLowerCase().replace(/_/g, ' ')}
            </Badge>
            <span>
              {t.category.toLowerCase()} · {t.language}
            </span>
            {t.qualityScore && (
              <Badge tone={QUALITY_TONE[t.qualityScore] ?? 'neutral'}>
                quality {t.qualityScore.toLowerCase()}
              </Badge>
            )}
          </div>
          {t.rejectedReason && <Alert tone="critical">{t.rejectedReason}</Alert>}
          <div className="template-preview">
            {header && (
              <strong>
                {header.format === 'TEXT' ? header.text : `[${header.format} header]`}
              </strong>
            )}
            <div className="template-preview__body">{body?.text}</div>
            {footer && <small>{footer.text}</small>}
            {buttons.map((b, i) => (
              <div key={i} className="table__secondary">
                {b.type === 'URL' ? '🔗' : b.type === 'PHONE_NUMBER' ? '📞' : '↩'} {b.text}
                {b.url ? ` — ${b.url}` : ''}
                {b.phone_number ? ` — ${b.phone_number}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

const WEBHOOK_ADMIN_EMAIL = 'mymail@theantony.in';
const TEMPLATE_PAGE_SIZE = 10;

const TEMPLATE_TONE: Record<string, Tone> = {
  APPROVED: 'success',
  PENDING: 'warning',
  IN_APPEAL: 'warning',
  PAUSED: 'warning',
  REJECTED: 'critical',
  DISABLED: 'critical',
  DELETED: 'neutral',
};

const QUALITY_TONE: Record<string, Tone> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'critical',
};

type WhatsappView = 'accounts' | 'templates' | 'messages';

const VIEW_HEADER: Record<WhatsappView, { title: string; description: string }> = {
  accounts: {
    title: 'WhatsApp accounts',
    description: 'Connect WhatsApp Business accounts and phone numbers.',
  },
  templates: {
    title: 'Message templates',
    description:
      'Create templates, submit them to Meta for review and keep them in sync. Approved templates are used in messages, campaigns and flows.',
  },
  messages: {
    title: 'WhatsApp messages',
    description:
      'Send a message, follow recent conversations, manage customer consent and set up automatic order messages.',
  },
};

/** WhatsApp Business accounts, templates and messaging, one sub-page per `view`. */
export function WhatsappPage({ view }: { view: WhatsappView }) {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const status = useLoader(() => api.get<WhatsappStatus>('/whatsapp/status'), tenantId);
  const accounts = useLoader(
    () => api.get<{ accounts: WhatsappAccountRow[] }>('/whatsapp/accounts').then((r) => r.accounts),
    tenantId,
  );
  const templates = useLoader(
    () =>
      api.get<{ templates: WhatsappTemplateRow[] }>('/whatsapp/templates').then((r) => r.templates),
    tenantId,
  );
  const manage = can('channels:manage');
  const isWebhookAdmin = session.user.email.toLowerCase() === WEBHOOK_ADMIN_EMAIL;
  const [templatePage, setTemplatePage] = useState(0);
  const templateCount = templates.data?.length ?? 0;
  useEffect(() => {
    const last = Math.max(0, Math.ceil(templateCount / TEMPLATE_PAGE_SIZE) - 1);
    setTemplatePage((p) => Math.min(p, last));
  }, [templateCount]);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [viewing, setViewing] = useState<WhatsappTemplateRow | null>(null);
  const [editing, setEditing] = useState<WhatsappTemplateRow | null>(null);

  const removeTemplate = async (t: WhatsappTemplateRow) => {
    const ok = await confirm({
      title: `Delete ${t.name} (${t.language})?`,
      message:
        'It is deleted on Meta too and can no longer be sent. Automations or campaigns that use it will stop working.',
      confirmLabel: 'Delete template',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/whatsapp/templates/${t.id}`);
      toast.success('Template deleted');
      void templates.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const sync = async (a: WhatsappAccountRow) => {
    setSyncing(a.id);
    try {
      const r = await api.post<{ templates: number }>(
        `/whatsapp/accounts/${a.id}/templates/sync`,
        {},
      );
      toast.success(`Synced ${r.templates} template${r.templates === 1 ? '' : 's'}`);
      void templates.reload();
      void accounts.reload();
    } catch (err) {
      toast.error(errorMessage(err));
      void accounts.reload();
    } finally {
      setSyncing(null);
    }
  };

  const copyToken = async (a: WhatsappAccountRow) => {
    setSyncing(`tok-${a.id}`);
    try {
      const r = await api.post<{ accessToken: string }>(
        `/whatsapp/accounts/${a.id}/access-token`,
        {},
      );
      await navigator.clipboard.writeText(r.accessToken);
      toast.success('Access token copied. The copy is recorded in the audit log.');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSyncing(null);
    }
  };

  const retryTata = async (a: WhatsappAccountRow) => {
    setSyncing(`tata-${a.id}`);
    try {
      const r = await api.post<{ tried: number; assigned: number }>(
        `/whatsapp/accounts/${a.id}/tata-credit-line`,
        {},
      );
      if (r.tried === 0) toast.success('Nothing to assign');
      else if (r.assigned === r.tried) toast.success('Credit line assigned');
      else toast.error(`Assigned ${r.assigned} of ${r.tried}; see the error shown per number`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSyncing(null);
      void accounts.reload();
    }
  };

  const recheckCreditLine = async (a: WhatsappAccountRow) => {
    setSyncing(`cl-${a.id}`);
    try {
      const r = await api.post<{ creditLine: { status: string; message?: string } }>(
        `/whatsapp/accounts/${a.id}/credit-line`,
        {},
      );
      if (r.creditLine.status === 'attached') toast.success('Credit line attached and verified');
      else toast.error(r.creditLine.message ?? 'Credit line is not attached');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSyncing(null);
      void accounts.reload();
    }
  };

  const remove = async (a: WhatsappAccountRow) => {
    const ok = await confirm({
      title: 'Remove WhatsApp account',
      message: `Remove ${a.name} (${a.wabaId}) from Cartly360? Its stored access token is deleted. The account itself stays in Meta.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/whatsapp/accounts/${a.id}`);
      toast.success('Account removed');
      void accounts.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if ((status.loading && !status.data) || (accounts.loading && !accounts.data)) {
    return <Spinner size="lg" label="Loading" />;
  }
  if (status.error || !status.data) {
    return <Alert tone="critical">{status.error ?? 'Failed to load'}</Alert>;
  }
  if (accounts.error || !accounts.data) {
    return <Alert tone="critical">{accounts.error ?? 'Failed to load'}</Alert>;
  }
  const cfg = status.data;
  const list = accounts.data;

  return (
    <div className="stack-lg">
      <PageHeader title={VIEW_HEADER[view].title} description={VIEW_HEADER[view].description} />

      {!cfg.configured && (
        <Alert tone="warning" title="Pending configuration">
          The Meta app is not configured on this server. Set <code>META_APP_ID</code> and{' '}
          <code>META_APP_SECRET</code> and restart the services to enable connections.
        </Alert>
      )}

      {view === 'accounts' && list.length === 0 && (
        <Card
          title="Embedded Signup"
          subtitle="Connect your own WhatsApp Business number through Meta's hosted flow."
        >
          {cfg.configured && cfg.appId && cfg.embeddedSignup.configId && cfg.graphVersion ? (
            manage ? (
              <EmbeddedSignup
                appId={cfg.appId}
                configId={cfg.embeddedSignup.configId}
                solutionId={cfg.embeddedSignup.solutionId}
                graphVersion={cfg.graphVersion}
                onConnected={() => void accounts.reload()}
              />
            ) : (
              <p className="text-muted">You need the “manage channels” permission to connect.</p>
            )
          ) : (
            <Alert tone="info" title="Pending configuration">
              Set <code>META_APP_ID</code>, <code>META_APP_SECRET</code> and{' '}
              <code>META_EMBEDDED_SIGNUP_CONFIG_ID</code> on the server to enable Embedded Signup.
            </Alert>
          )}
        </Card>
      )}

      {view === 'accounts' && (
        <>
          <Card
            title="Connected accounts"
            subtitle="Each WhatsApp Business Account (WABA) can have several phone numbers."
            flush
          >
            {list.length === 0 ? (
              <EmptyState icon={<MessageCircle size={22} />} title="No WhatsApp account connected">
                Connect an account above to see its phone numbers and quality rating here.
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Phone numbers</th>
                      <th>Status</th>
                      <th>Connected</th>
                      <th className="table__actions">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div className="table__primary">{a.name}</div>
                          <div className="table__secondary mono">{a.wabaId}</div>
                        </td>
                        <td>
                          {a.phoneNumbers.length === 0 ? (
                            <span className="text-muted">None on this account</span>
                          ) : (
                            a.phoneNumbers.map((p) => (
                              <div key={p.id} className="row">
                                <span className="mono">{p.displayPhoneNumber}</span>
                                {p.isOnBizApp && <BizAppBadge />}
                                {p.verifiedName && (
                                  <span className="text-muted">{p.verifiedName}</span>
                                )}
                                {p.qualityRating && (
                                  <Badge tone={QUALITY_TONE[p.qualityRating] ?? 'neutral'}>
                                    {p.qualityRating.toLowerCase()}
                                  </Badge>
                                )}
                                {p.messagingLimitTier && (
                                  <span className="badge mono">{p.messagingLimitTier}</span>
                                )}
                              </div>
                            ))
                          )}
                        </td>
                        <td>
                          <StatusBadge status={a.status} />
                          {a.needsReauth && (
                            <div className="table__secondary">
                              Meta rejected the token — reconnect
                            </div>
                          )}
                        </td>
                        <td>{formatRelative(a.createdAt)}</td>
                        <td className="table__actions">
                          {manage && (
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<RefreshCw size={14} />}
                              loading={syncing === a.id}
                              disabled={a.needsReauth}
                              onClick={() => void sync(a)}
                              aria-label={`Sync templates for ${a.name}`}
                            >
                              Sync templates
                            </Button>
                          )}
                          {manage && (
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<Trash2 size={14} />}
                              onClick={() => void remove(a)}
                              aria-label={`Remove ${a.name}`}
                            >
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {list.map((a) => (
            <Card
              key={`details-${a.id}`}
              title={`Connection details · ${a.name}`}
              subtitle="The identifiers Meta uses for this account (Meta for Developers → WhatsApp → API setup)."
            >
              <div className="stack">
                <div className="detail-grid">
                  <CopyField label="WhatsApp Business Account ID (WABA ID)" value={a.wabaId} />
                  {cfg.appId && <CopyField label="Meta App ID" value={cfg.appId} />}
                  <CopyField label="Webhook callback URL" value={cfg.webhookUrl} />
                </div>
                <div className="row">
                  <Badge tone="neutral">
                    {a.method === 'embedded_signup' ? 'Embedded Signup' : 'Manual token'}
                  </Badge>
                  <StatusBadge status={a.status} />
                  <span className="text-muted">
                    Webhook subscribed:{' '}
                    {a.webhookSubscribedAt ? formatRelative(a.webhookSubscribedAt) : 'not yet'}
                  </span>
                  <span className="text-muted">
                    Templates synced:{' '}
                    {a.templatesSyncedAt ? formatRelative(a.templatesSyncedAt) : 'never'}
                  </span>
                  {cfg.graphVersion && <span className="badge mono">{cfg.graphVersion}</span>}
                </div>
                <div className="row">
                  <span className="text-muted">
                    Access token: <code>{a.tokenHint ?? 'unavailable'}</code>
                    {a.needsReauth ? ' — Meta rejected it; reconnect the account.' : ''}
                  </span>
                  {manage && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Copy size={14} />}
                      loading={syncing === `tok-${a.id}`}
                      onClick={() => void copyToken(a)}
                    >
                      Copy access token
                    </Button>
                  )}
                </div>
                {a.phoneNumbers.length === 0 ? (
                  <p className="text-muted">No phone numbers on this account.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Phone number</th>
                          <th>Phone Number ID</th>
                          <th>Verified name</th>
                          <th>Quality</th>
                          <th>Messaging limit</th>
                          <th>Cloud API</th>
                          {cfg.tataCreditConfigured && <th>Tata credit line</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {a.phoneNumbers.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <span className="mono">{p.displayPhoneNumber}</span>
                              {p.isOnBizApp && (
                                <div>
                                  <BizAppBadge />
                                </div>
                              )}
                            </td>
                            <td className="mono">{p.phoneNumberId}</td>
                            <td>{p.verifiedName ?? '—'}</td>
                            <td>
                              {p.qualityRating ? (
                                <Badge tone={QUALITY_TONE[p.qualityRating] ?? 'neutral'}>
                                  {p.qualityRating.toLowerCase()}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="mono">{p.messagingLimitTier ?? '—'}</td>
                            <td>
                              {p.isOnBizApp
                                ? 'Coexistence · no registration needed'
                                : p.registeredAt
                                  ? `Registered ${formatRelative(p.registeredAt)}`
                                  : 'Not registered'}
                            </td>
                            {cfg.tataCreditConfigured && (
                              <td>
                                {p.tataCredit.status === 'assigned' ? (
                                  <Badge tone="success">assigned</Badge>
                                ) : p.tataCredit.status === 'failed' ? (
                                  <Badge tone="critical">failed</Badge>
                                ) : (
                                  <Badge tone="neutral">pending</Badge>
                                )}
                                {p.tataCredit.error && (
                                  <div className="table__secondary">{p.tataCredit.error}</div>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {cfg.tataCreditConfigured &&
                  manage &&
                  a.phoneNumbers.some((p) => p.tataCredit.status !== 'assigned') && (
                    <div>
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={syncing === `tata-${a.id}`}
                        onClick={() => void retryTata(a)}
                      >
                        Assign Tata credit line
                      </Button>
                    </div>
                  )}
                {cfg.creditLineConfigured && (
                  <div className="stack">
                    <div className="row">
                      <strong>Credit line</strong>
                      {a.creditLine.status === null ? (
                        <Badge tone="neutral">not checked</Badge>
                      ) : (
                        <Badge
                          tone={
                            a.creditLine.status === 'attached'
                              ? 'success'
                              : a.creditLine.status === 'failed'
                                ? 'critical'
                                : 'warning'
                          }
                        >
                          {a.creditLine.status.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      {a.creditLine.checkedAt && (
                        <span className="text-muted">
                          checked {formatRelative(a.creditLine.checkedAt)}
                        </span>
                      )}
                    </div>
                    {a.creditLine.error && <Alert tone="warning">{a.creditLine.error}</Alert>}
                    {(a.creditLine.creditLineId ||
                      a.creditLine.allocationConfigId ||
                      a.creditLine.primaryFundingId) && (
                      <div className="detail-grid">
                        {a.creditLine.creditLineId && (
                          <CopyField label="Credit line ID" value={a.creditLine.creditLineId} />
                        )}
                        {a.creditLine.allocationConfigId && (
                          <CopyField
                            label="Allocation config ID"
                            value={a.creditLine.allocationConfigId}
                          />
                        )}
                        {a.creditLine.primaryFundingId && (
                          <CopyField
                            label="Primary funding ID"
                            value={a.creditLine.primaryFundingId}
                          />
                        )}
                      </div>
                    )}
                    {manage &&
                      a.method === 'embedded_signup' &&
                      a.creditLine.status !== 'attached' && (
                        <div>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => void recheckCreditLine(a)}
                            loading={syncing === `cl-${a.id}`}
                          >
                            Check credit line again
                          </Button>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </>
      )}

      {view === 'templates' && manage && (
        <TemplateCreate
          key={editing?.id ?? 'new'}
          accounts={list.map((x) => ({ id: x.id, name: x.name, wabaId: x.wabaId }))}
          onCreated={() => void templates.reload()}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
        />
      )}

      {view === 'templates' && (
        <Card
          title="Your templates"
          subtitle="Mirrored from Meta. Status changes arrive by webhook; use Sync to pull the full list."
          actions={
            manage && list.length > 0 ? (
              <div className="row">
                {list.map((x) => (
                  <Button
                    key={x.id}
                    size="sm"
                    variant="secondary"
                    icon={<RefreshCw size={14} />}
                    loading={syncing === x.id}
                    onClick={() => void sync(x)}
                  >
                    {list.length > 1 ? `Sync ${x.name}` : 'Sync templates'}
                  </Button>
                ))}
              </div>
            ) : undefined
          }
          flush
        >
          {!templates.data || templates.data.length === 0 ? (
            <EmptyState icon={<MessageCircle size={22} />} title="No templates yet">
              Connect an account and press Sync templates to load its approved and pending
              templates.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {templates.data
                    .slice(
                      templatePage * TEMPLATE_PAGE_SIZE,
                      (templatePage + 1) * TEMPLATE_PAGE_SIZE,
                    )
                    .map((t) => (
                      <tr key={t.id}>
                        <td>
                          <div className="table__primary mono">{t.name}</div>
                          <div className="table__secondary">{t.language}</div>
                        </td>
                        <td>{t.category.toLowerCase()}</td>
                        <td>
                          <Badge tone={TEMPLATE_TONE[t.status] ?? 'neutral'}>
                            {t.status.toLowerCase().replace(/_/g, ' ')}
                          </Badge>
                          {t.rejectedReason && (
                            <div className="table__secondary">{t.rejectedReason}</div>
                          )}
                        </td>
                        <td>{formatRelative(t.updatedAt)}</td>
                        <td>
                          <div className="row">
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Eye size={14} />}
                              aria-label={`View ${t.name}`}
                              title="View"
                              onClick={() => setViewing(t)}
                            />
                            {manage && (
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<Pencil size={14} />}
                                aria-label={`Edit ${t.name}`}
                                title={
                                  !EDITABLE_STATUSES.includes(t.status)
                                    ? 'Only approved, rejected or paused templates can be edited'
                                    : !isFormEditable(t.components)
                                      ? 'Templates with an image, video or document header cannot be edited here'
                                      : 'Edit'
                                }
                                disabled={
                                  !EDITABLE_STATUSES.includes(t.status) ||
                                  !isFormEditable(t.components)
                                }
                                onClick={() => {
                                  setEditing(t);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              />
                            )}
                            {manage && t.status !== 'DELETED' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<Trash2 size={14} />}
                                aria-label={`Delete ${t.name}`}
                                title="Delete"
                                onClick={() => void removeTemplate(t)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {templateCount > TEMPLATE_PAGE_SIZE && (
            <Pager
              page={templatePage}
              pageSize={TEMPLATE_PAGE_SIZE}
              total={templateCount}
              onChange={setTemplatePage}
            />
          )}
        </Card>
      )}

      <TemplateView t={viewing} onClose={() => setViewing(null)} />

      {view === 'messages' && (
        <>
          <MessagingSections
            tenantId={tenantId}
            phones={list.flatMap((a) =>
              a.phoneNumbers.map((p) => ({
                id: p.id,
                label: `${p.displayPhoneNumber}${p.verifiedName ? ` · ${p.verifiedName}` : ''}`,
                registeredAt: p.registeredAt,
                coexistence: p.isOnBizApp,
              })),
            )}
            templates={(templates.data ?? []).map((t) => ({
              name: t.name,
              language: t.language,
              status: t.status,
              bodyParams: countBodyParams(t.components),
              urlButton: dynamicUrlButton(t.components),
            }))}
            canSend={can('inbox:reply')}
            canReadMessages={can('inbox:read')}
            canReadConsent={can('customers:read')}
            canManageConsent={can('customers:manage')}
            canManageChannels={manage}
            onPhonesChanged={() => void accounts.reload()}
          />

          <CampaignSections
            show="orderMessages"
            tenantId={tenantId}
            phones={list.flatMap((a) =>
              a.phoneNumbers.map((p) => ({
                id: p.id,
                label: `${p.displayPhoneNumber}${p.verifiedName ? ` · ${p.verifiedName}` : ''}`,
                registeredAt: p.registeredAt,
                coexistence: p.isOnBizApp,
              })),
            )}
            templates={(templates.data ?? []).map((t) => ({
              name: t.name,
              language: t.language,
              status: t.status,
              bodyParams: countBodyParams(t.components),
            }))}
            canManageChannels={manage}
            canReadCampaigns={can('campaigns:read')}
            canManageCampaigns={can('campaigns:manage')}
          />
        </>
      )}

      {view === 'accounts' && isWebhookAdmin && (
        <Card
          title="Webhook"
          subtitle="Enter these in Meta for Developers → your app → WhatsApp → Configuration."
        >
          <div className="stack">
            <CopyField label="Callback URL" value={cfg.webhookUrl} />
            <p className="text-muted">
              Verify token: {cfg.webhookVerifyTokenSet ? 'set on the server' : 'not set'} (
              <code>WHATSAPP_WEBHOOK_VERIFY_TOKEN</code>). Subscribe to the <code>messages</code>,{' '}
              <code>message_template_status_update</code> and{' '}
              <code>message_template_quality_update</code> fields. Graph API version:{' '}
              <code>{cfg.graphVersion ?? 'n/a'}</code>.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
